import { set, type FieldValues, type Path, type Resolver, type UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { errorMessage, HttpError } from '@/lib/api';

/**
 * Resolver Zod com normalização opcional dos valores do formulário (ex.: '' -> null, máscara -> centavos)
 * antes de validar com o schema dos contracts. Devolve os erros aninhados (`address.cep`).
 */
export function zodForm<T extends FieldValues>(schema: z.ZodType, normalize?: (v: T) => unknown, rename: Record<string, string> = {}): Resolver<T> {
  return async (values) => {
    const r = schema.safeParse(normalize ? normalize(values) : values);
    if (r.success) return { values, errors: {} };
    const errors: Record<string, unknown> = {};
    for (const issue of r.error.issues) {
      const raw = issue.path.join('.');
      const path = rename[raw] ?? raw;
      if (!path || (errors as Record<string, unknown>)[path]) continue;
      set(errors, path, { type: issue.code, message: issue.message });
    }
    return { values: {}, errors } as never;
  };
}

/** Mapeia os erros 4xx do servidor para os campos; o que não casar vira toast. */
export function applyServerError<T extends FieldValues>(form: UseFormReturn<T>, e: unknown, rename: Record<string, string> = {}) {
  if (e instanceof HttpError) {
    const fields = e.fieldErrors();
    const known = Object.entries(fields).filter(([k]) => k);
    if (known.length) {
      for (const [k, msg] of known) form.setError((rename[k] ?? k) as Path<T>, { type: 'server', message: msg });
      return;
    }
    if (e.code === 'slug_taken') {
      form.setError('slug' as Path<T>, { type: 'server', message: e.message });
      return;
    }
  }
  toast.error(errorMessage(e));
}

export const emptyToNull = (v: string | null | undefined) => (v && v.trim() ? v.trim() : null);

export const hhmm = (t: string) => t.slice(0, 5);
